/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Col,
  Edit,
  Row,
  SaveButton,
  useForm,
  useSelect,
} from "@pankod/refine-antd";
import {
  IResourceComponentsProps,
  useMutation,
  useNavigation,
  useNotification,
} from "@pankod/refine-core";
import {
  IShootExecutionForm,
  ShootExecutionForm,
} from "components/forms/frm_shootexecution";
import dayjs from "dayjs";
import { ShootExecutionStatus } from "enums";
import { ICampaignShoot } from "interfaces";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import {
  executeShoot as _executeShoot,
  uploadExecutionFile,
} from "services/shoot-execution";
import { extractApiErrorMessage } from "utils/utils";

const normalizeShootObject = (value: ICampaignShoot) => ({
  campaign_id: value.campaign_id,
  negative_cycle_days: value.negative_cycle_days,
  rate_per_minute: value.rate_per_minute,
  templates_schedule:
    value.templates_schedule?.map((template) => ({
      template_name: template.template_name,
      scheduled_datetime: dayjs(template.scheduled_datetime).isValid()
        ? dayjs(template.scheduled_datetime).format("YYYY-MM-DDTHH:mm:00-03:00")
        : null,
    })) || [],
});

export const ShootExecutionEdit: FC<IResourceComponentsProps> = () => {
  const [initiated, setInitiated] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const { goBack } = useNavigation();
  const { open } = useNotification();

  const {
    selectProps: campaignSelectProps,
    queryResult: { data: campaigns },
  } = useSelect({
    resource: "v1/repique-campaign",
    optionLabel: "name",
    optionValue: "_id",
  });

  const { formProps, saveButtonProps, queryResult, onFinish, formLoading } =
    useForm({
      metaData: {
        method: "PATCH",
      },
      redirect: false,
      successNotification: false,
      errorNotification: false,
      onMutationError: (error) => {
        const message = extractApiErrorMessage(error);

        open?.({
          type: "error",
          description: "Erro ao editar disparo!",
          message,
        });
      },
    });

  const { mutate: uploadFile, isLoading: uploadFileLoading } = useMutation({
    mutationFn: uploadExecutionFile,
  });

  const { mutate: executeShoot, isLoading: executeShootLoading } = useMutation({
    mutationFn: _executeShoot,
  });

  const handleFinish = async (values: IShootExecutionForm) => {
    const templates_schedule = values.templates_schedule.map(
      ({ scheduled_datetime, order }) => ({
        order,
        scheduled_datetime: scheduled_datetime.format(
          "YYYY-MM-DDTHH:mm:ss-03:00"
        ),
      })
    );

    const firstTemplate = templates_schedule.reduce((prev, curr) => {
      if (prev.order < curr.order) {
        return prev;
      }
      return curr;
    });

    if (!firstTemplate) return;

    const payload = {
      active: true,
      campaign_id: values.campaign_id,
      templates_schedule,
      rate_per_minute: values.rate_per_minute,
      negative_cycle_days: values.negative_cycle_days,
    };

    const updateResponse = await onFinish?.(payload);
    const shootId = updateResponse?.data?._id;

    if (!shootId) return;

    const currentFile = values.leads_file;

    if (!currentFile || currentFile.size === 0) {
      open?.({
        type: "success",
        message: "Disparo agendado com sucesso",
      });

      return;
    }

    uploadFile(
      {
        file: values.leads_file,
        shootId,
      },
      {
        onSuccess: () => {
          open?.({
            type: "success",
            message: "Disparo agendado com sucesso",
          });
        },
        onError: (error) => {
          const message = extractApiErrorMessage(error);

          open?.({
            type: "error",
            message,
          });
        },
      }
    );

    setHasEdited(false);
  };

  const handleExecuteShoot = async () => {
    const shootId = queryResult?.data?.data?._id;

    if (!shootId) return;

    executeShoot(
      {
        shootId,
      },
      {
        onSuccess: () => {
          open?.({
            type: "success",
            message: "Disparo executado com sucesso",
          });
        },
        onError: (error) => {
          const message = extractApiErrorMessage(error);

          open?.({
            type: "error",
            message,
          });
        },
      }
    );
  };

  const handleFormChange = () => {
    if (hasEdited || !initiated) return;

    const formValues = formProps.form?.getFieldsValue();
    if (!formValues || !queryResult?.data?.data) return;

    const clonedFormValues = _.cloneDeep(formValues);
    const clonedQueryData = _.cloneDeep(queryResult.data.data);

    const normalizedFormValues = normalizeShootObject(
      clonedFormValues as ICampaignShoot
    );

    const currentValues = normalizeShootObject(
      clonedQueryData as ICampaignShoot
    );

    const isEqual = _.isEqual(normalizedFormValues, currentValues);

    setHasEdited(!isEqual);
  };

  useEffect(() => {
    if (!!queryResult?.data?.data._id && !initiated) {
      setInitiated(true);
    }
  }, [queryResult?.data?.data]);

  const isLoading = formLoading || uploadFileLoading || executeShootLoading;

  return (
    <Edit
      title="Editar Disparo"
      headerButtons={[]}
      footerButtons={() => (
        <Row gutter={[6, 6]}>
          <Col>
            <Button onClick={() => goBack()} style={{ minWidth: "92px" }}>
              Cancelar
            </Button>
          </Col>

          <Col>
            <SaveButton
              {...saveButtonProps}
              loading={isLoading}
              disabled={isLoading}
              icon={null}
              style={{ minWidth: "92px" }}
            >
              Salvar
            </SaveButton>
          </Col>

          <Col>
            <Button
              className="execute-btn"
              onClick={handleExecuteShoot}
              loading={isLoading}
              disabled={
                queryResult?.data?.data?.status !==
                  ShootExecutionStatus.DRAFT ||
                isLoading ||
                hasEdited
              }
              style={{ whiteSpace: "nowrap" }}
            >
              Executar Disparo
            </Button>
          </Col>
        </Row>
      )}
    >
      <ShootExecutionForm
        formProps={{
          ...formProps,
          onValuesChange: handleFormChange,
          onFinish: handleFinish,
        }}
        queryResult={queryResult}
        campaignSelectProps={campaignSelectProps}
        campaigns={campaigns?.data}
        formLoading={isLoading}
        onFileChange={() => setHasEdited(true)}
      />
    </Edit>
  );
};
